import { useState } from 'react';
import { useTranslation } from '@/i18n';
import { isCardOnly } from '@/payment/cardOnly';
import { signalTabletCompletion } from '@/payment/tabletCompletion';

/**
 * The end of the flow, for the tablet only.
 *
 * THERE IS NO END OF THE FLOW ON A PHONE. The receipt screen is the last thing a guest sees; they read it,
 * maybe text themselves a copy, maybe answer the feedback questions, and then close the tab. Nothing in the
 * app is "finished", because on a phone nothing needs to be.
 *
 * The tablet does need it. On the handheld's "Pay here" path this runs in a WebView the staff want back, and
 * the tablet cannot advance on the payment event without pulling the receipt out from under the guest
 * mid-read. So it waits to be told - see docs/request-tablet-completion-signal.md.
 *
 * WHICH LEAVES THE QUESTION OF WHAT "TOLD" MEANS, and the answer is a button rather than an inference.
 * Everything above this on the receipt screen is optional and unordered: a guest may send themselves a copy
 * or not, answer the questions or not, in either order. There is no combination of those that means
 * "finished" - a guest who ignores both is the commonest case of all, and inferring completion from a
 * dismissal would end the session for someone who simply scrolled past. Asking is honest and it is one tap.
 *
 * Renders nothing at all on the phone path, where the sentinel must never fire.
 */
export function TabletDone() {
    const { t } = useTranslation();
    const [finished, setFinished] = useState(false);

    if (!isCardOnly()) return null;

    return (
        <button
            type="button"
            /*
             * Latched rather than merely idempotent. The WebView swallows the navigation and stays put until
             * the tablet gets around to swapping the screen, so a guest who taps twice on a slow moment would
             * otherwise fire it twice - harmless for the tablet, but the button looking dead is what makes
             * people tap again in the first place.
             */
            disabled={finished}
            onClick={() => {
                setFinished(true);
                signalTabletCompletion();
            }}
            className="inline-flex min-h-12 items-center justify-center rounded-xl bg-primary px-5 py-3 font-semibold text-primary-foreground shadow-xs disabled:opacity-60"
        >
            {finished ? t('receipt.doneFinishing') : t('receipt.done')}
        </button>
    );
}
